import { Avatar, Box, Button, ButtonBase, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { generateInitials } from "utils/string";

import { UploadedBrand } from "../schema";
import styles from "./styles";

interface Props {
  brands: UploadedBrand[];
  onBack: () => void;
  onShareWithCommunity: () => Promise<void>;
  onKeepPrivate: () => void;
  onDecideLater: () => void;
}

const obfuscateEmail = (email: string): string => {
  const at = email.indexOf("@");
  if (at <= 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  if (local.length <= 1) return `${local}***${domain}`;
  return `${local[0]}***${domain}`;
};

export type UserSuppliedContact = {
  bentoBrandId: number;
  brandName: string;
  brandLogoUrl?: string | null;
  brandLogoColor?: string | null;
  email: string;
};

export const collectUserSuppliedContacts = (
  brands: UploadedBrand[],
): UserSuppliedContact[] => {
  const out: UserSuppliedContact[] = [];
  for (const brand of brands) {
    const first = brand.contacts[0];
    // A contact with no `id` is the user-supplied PreviewContact prepended by
    // the backend when the email didn't match any existing BentoContact.
    if (!first || first.id) continue;
    out.push({
      bentoBrandId: brand.bentoBrand.id,
      brandName:
        brand.bentoBrand.brandName || brand.bentoBrand.normalizedName || "",
      brandLogoUrl: brand.bentoBrand.logoUrl,
      brandLogoColor: brand.bentoBrand.backgroundLogoColor,
      email: first.email,
    });
  }
  return out;
};

const YourContacts = ({
  brands,
  onBack,
  onShareWithCommunity,
  onKeepPrivate,
  onDecideLater,
}: Props) => {
  const contacts = useMemo(() => collectUserSuppliedContacts(brands), [brands]);
  const count = contacts.length;
  const heading =
    count === 1 ? "You've added 1 contact" : `You've added ${count} contacts`;
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      await onShareWithCommunity();
    } finally {
      setSharing(false);
    }
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Button disableRipple sx={styles.backButton} onClick={onBack}>
          <Box
            component="i"
            className="fa-regular fa-arrow-left"
            sx={styles.backIcon}
          />
          Edit list
        </Button>
        <Typography sx={styles.title}>Your Contacts</Typography>
      </Box>

      <Box sx={styles.body}>
        <Box sx={styles.intro}>
          <Box sx={styles.introBadge}>
            <Box
              component="i"
              className="fa-regular fa-user-group"
              sx={styles.introIcon}
            />
          </Box>
          <Typography sx={styles.introHeading}>{heading}</Typography>
          <Typography sx={styles.introCopy}>
            These contacts aren&apos;t in Bento&apos;s database yet. What would
            you like to do with them?
          </Typography>
        </Box>

        <Box sx={styles.contactsCard}>
          <Typography sx={styles.contactsLabel}>Your contacts</Typography>
          {contacts.map((contact) => (
            <Box key={contact.bentoBrandId} sx={styles.contactRow}>
              <Avatar
                src={contact.brandLogoUrl || undefined}
                sx={{
                  ...styles.contactAvatar,
                  backgroundColor: contact.brandLogoColor || "#f6c8d0",
                }}
              >
                {generateInitials(contact.brandName)}
              </Avatar>
              <Typography sx={styles.contactName}>
                {contact.brandName}
              </Typography>
              <Typography sx={styles.contactEmail}>
                {obfuscateEmail(contact.email)}
              </Typography>
            </Box>
          ))}
        </Box>

        <ButtonBase
          disabled={sharing}
          onClick={handleShare}
          sx={{
            ...styles.optionCard,
            ...styles.optionCardPrimary,
            ...styles.optionCardClickable,
          }}
        >
          <Box sx={styles.optionIconWrap}>
            <Box
              component="i"
              className="fa-regular fa-globe"
              sx={styles.optionIcon}
            />
          </Box>
          <Box sx={styles.optionContent}>
            <Typography sx={styles.optionTitlePrimary}>
              Share with the community
            </Typography>
            <Typography sx={styles.optionCopyPrimary}>
              Contribute your contacts and unlock thousands shared by other
              creators on Bento.
            </Typography>
            <Box sx={styles.unlocksRow}>
              <Box
                component="i"
                className="fa-regular fa-sparkles"
                sx={styles.unlocksIcon}
              />
              <Typography sx={styles.unlocksLabel}>
                Unlocks community contacts
              </Typography>
            </Box>
          </Box>
          <Box
            component="i"
            className="fa-regular fa-chevron-right"
            sx={styles.chevron}
          />
        </ButtonBase>

        <ButtonBase
          onClick={onKeepPrivate}
          sx={{ ...styles.optionCard, ...styles.optionCardClickable }}
        >
          <Box
            sx={{ ...styles.optionIconWrap, ...styles.optionIconWrapNeutral }}
          >
            <Box
              component="i"
              className="fa-regular fa-lock"
              sx={styles.optionIconNeutral}
            />
          </Box>
          <Box sx={styles.optionContent}>
            <Typography sx={styles.optionTitleNeutral}>Keep private</Typography>
            <Typography sx={styles.optionCopyNeutral}>
              Only you can see and use these contacts. They won&apos;t be shared
              with anyone else.
            </Typography>
          </Box>
        </ButtonBase>

        <Button disableRipple sx={styles.decideLater} onClick={onDecideLater}>
          Decide later
        </Button>
      </Box>
    </Box>
  );
};

export default YourContacts;
